/**
 * HelpDrawer — slide-in help panel from the right, route-aware defaults.
 * Mirrors XTS HelpDrawer (Tailwind + animated panel) but uses PlayStudio's
 * design system CSS vars and inline styles.
 */

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { helpSections, getDefaultSection, getDefaultTopic } from "./helpData";
import { HelpNav } from "./HelpNav";
import { HelpContent } from "./HelpContent";

interface HelpDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchHit {
  sectionId: string;
  sectionTitle: string;
  topicId: string;
  topicTitle: string;
  snippet: string;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function buildSnippet(plainBody: string, query: string): string {
  const lc = plainBody.toLowerCase();
  const idx = lc.indexOf(query.toLowerCase());
  if (idx < 0) return plainBody.slice(0, 140) + (plainBody.length > 140 ? "…" : "");
  const start = Math.max(0, idx - 40);
  const end = Math.min(plainBody.length, idx + query.length + 80);
  return (start > 0 ? "…" : "") + plainBody.slice(start, end) + (end < plainBody.length ? "…" : "");
}

export function HelpDrawer({ isOpen, onClose }: HelpDrawerProps) {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(() =>
    getDefaultSection(location.pathname),
  );
  const [activeTopic, setActiveTopic] = useState(() =>
    getDefaultTopic(location.pathname),
  );
  const [searchQuery, setSearchQuery] = useState("");

  // Re-sync defaults when the route changes while the drawer is closed.
  useEffect(() => {
    if (!isOpen) {
      setActiveSection(getDefaultSection(location.pathname));
      setActiveTopic(getDefaultTopic(location.pathname));
      setSearchQuery("");
    }
  }, [location.pathname, isOpen]);

  // Escape closes the drawer.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId);
    const section = helpSections.find((s) => s.id === sectionId);
    if (section && section.topics.length > 0) {
      setActiveTopic(section.topics[0]!.id);
    }
  };

  const currentSection = helpSections.find((s) => s.id === activeSection);

  const trimmedQuery = searchQuery.trim();
  const searchHits: SearchHit[] = trimmedQuery.length >= 2
    ? helpSections.flatMap((section) =>
        section.topics
          .map((topic): SearchHit | null => {
            const plainTitle = stripHtml(topic.title);
            const plainBody = stripHtml(topic.body);
            const haystack = `${plainTitle} ${plainBody}`.toLowerCase();
            if (!haystack.includes(trimmedQuery.toLowerCase())) return null;
            return {
              sectionId: section.id,
              sectionTitle: stripHtml(section.title),
              topicId: topic.id,
              topicTitle: plainTitle,
              snippet: buildSnippet(plainBody, trimmedQuery),
            };
          })
          .filter((h): h is SearchHit => h !== null),
      )
    : [];

  const handleHitClick = (hit: SearchHit) => {
    setActiveSection(hit.sectionId);
    setActiveTopic(hit.topicId);
    setSearchQuery("");
  };

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 16, 22, 0.45)",
          zIndex: 950,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Help and Documentation"
        data-testid="help-drawer"
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          bottom: 0,
          width: "min(640px, 50vw)",
          background: "var(--bg)",
          display: "flex",
          flexDirection: "column",
          boxShadow: "-12px 0 32px rgba(0,0,0,0.18)",
          zIndex: 951,
          animation: "helpSlideIn 220ms ease-out",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 18px",
            borderBottom: "1px solid var(--border)",
            background: "var(--bg-soft)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <h2
              style={{
                fontSize: 15,
                fontWeight: 600,
                margin: 0,
                color: "var(--text)",
                letterSpacing: "-0.005em",
              }}
            >
              Help &amp; Documentation
            </h2>
            <span style={{ fontSize: 11, color: "var(--text-3)" }}>
              PlayStudio · v1
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="help-drawer-close"
            aria-label="Close help drawer"
            style={{
              background: "transparent",
              border: 0,
              color: "var(--text-3)",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
              padding: "4px 8px",
              borderRadius: 4,
            }}
          >
            ✕
          </button>
        </div>

        <div
          style={{
            padding: "10px 14px",
            borderBottom: "1px solid var(--border)",
            flexShrink: 0,
          }}
        >
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search help…"
            data-testid="help-search-input"
            aria-label="Search help and documentation"
            style={{
              width: "100%",
              padding: "7px 10px",
              fontSize: 12.5,
              background: "var(--bg-soft)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              outline: "none",
            }}
          />
        </div>

        {trimmedQuery.length >= 2 ? (
          <div
            data-testid="help-search-results"
            style={{
              flex: 1,
              overflowY: "auto",
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {searchHits.length === 0 ? (
              <p style={{ color: "var(--text-3)", fontSize: 13, padding: 8 }}>
                No matches for &ldquo;{trimmedQuery}&rdquo;.
              </p>
            ) : (
              searchHits.map((hit) => (
                <button
                  key={`${hit.sectionId}:${hit.topicId}`}
                  type="button"
                  onClick={() => handleHitClick(hit)}
                  data-testid={`help-search-hit-${hit.topicId}`}
                  style={{
                    textAlign: "left",
                    background: "var(--bg-soft)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "10px 12px",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                    {hit.sectionTitle}
                  </span>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>
                    {hit.topicTitle}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5 }}>
                    {hit.snippet}
                  </span>
                </button>
              ))
            )}
          </div>
        ) : (
          <>
            <HelpNav
              sections={helpSections}
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
            />

            {currentSection ? (
              <HelpContent
                section={currentSection}
                activeTopic={activeTopic}
                onTopicChange={setActiveTopic}
              />
            ) : null}
          </>
        )}
      </div>
      <style>{`
        @keyframes helpSlideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .help-body p { margin: 0 0 10px; }
        .help-body code {
          font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
          font-size: 11.5px;
          background: var(--bg-soft);
          padding: 1px 5px;
          border-radius: 3px;
          border: 1px solid var(--border);
        }
        .help-body ul, .help-body ol { padding-left: 18px; margin: 0 0 10px; }
        .help-body li { margin-bottom: 4px; }
        .help-body strong { color: var(--text); font-weight: 600; }
        .help-body em { font-style: italic; color: var(--text-2); }
        .help-body a { color: var(--accent); }
      `}</style>
    </>
  );
}
